import { useRef } from 'react';
import Select, { ActionMeta, SingleValue } from 'react-select';
import {
  hasValue,
  OTHEROPTION,
  scrollToCenter,
} from '@r-and-a-shared-ui/utils';
import styles from './FormDropdown.module.scss';

import { FormFieldProps, OptionProps } from '../FormField/FormField';
import { useController, useFormContext } from 'react-hook-form';
import { defaultValidationMessage } from '../config/validators/validators';

export interface FormDropdownProps extends FormFieldProps {
  onClear?: () => void;
}

export const FormDropdown = (
  props: FormDropdownProps,
) => {
  const selectRef = useRef<any | null>(null);
  const {
    name,
    fieldRef,
    placeholder,
    label,
    isDisabled,
    validationMessage,
    options,
    triggerError,
    entireObjectSelection,
    menuPlacement,
    onClear,
    hiddenClearable,
    hint,
  } = props;
  const {
    register,
    formState: { errors },
    control,
  } = useFormContext();

  const { field } = useController({ name, control });
  const {
    value: selectedOption,
    onChange: selectOnChange,
    ...restField
  } = field;

  const { ref, ...rest } = register(name);

  const getValue = () => {
    if (selectedOption === OTHEROPTION) {
      return {
        value: OTHEROPTION,
        label: OTHEROPTION,
      };
    }
    let selectionValue = selectedOption;
    if (entireObjectSelection) {
      selectionValue = selectedOption?.value || selectedOption;
    }
    const foundValue =
      options?.find(option => option.value === selectionValue) ?? null;

    return foundValue;
  };

  const onSelectChange = (
    option: SingleValue<OptionProps>,
    { action }: ActionMeta<OptionProps>,
  ) => {
    if (action === 'clear') {
      selectOnChange('');
      if (onClear) {
        onClear();
      }
      return;
    }
    selectOnChange(entireObjectSelection ? option : option?.value);
  };

  const addErrorClass = triggerError || !!errors?.[name];
  return (
    <div
      ref={fieldRef}
      className={`${styles['form-element']} ${styles['form-select']} ${
        addErrorClass ? styles['form-element__has-error'] : ''
      }`}
        onFocus={() => scrollToCenter(fieldRef)}
    >
      {hasValue(label) && (
        <label
          className={`${styles['form-element__label']} ${
            getValue() ? styles['form-element__has-value'] : ''
          }`}
          htmlFor={name}
        >
          {label}
        </label>
      )}
      <Select
        id={name}
        classNamePrefix={'custom__select'}
        placeholder={hasValue(label) ? label : placeholder}
        isClearable={!hiddenClearable}
        options={options || []}
        value={getValue()}
        {...restField}
        {...rest}
        ref={e => {
          ref(e);
          selectRef.current = e;
        }}
        onChange={onSelectChange}
        isDisabled={isDisabled}
        menuPlacement={menuPlacement}
      />
      <div className={styles['form-element__bar']}></div>
      {errors?.[name] ? (
        <small className={styles['form-element__hint']}>
          {validationMessage || defaultValidationMessage}
        </small>
      ) : (
        hint ? (
          <small className={styles['form-element__hint']}>
            {hint}
          </small>
        ) : null
      )}
    </div>
  );
};

export default FormDropdown;
